import type { ErrorReporter } from '../../common/ErrorReporter';
import DB, { MetainfoMVCC, ScheduledAction } from '../../db/DB';
import { ScheduledActions } from '../../ReplicantScheduledActions';
import { resolveActionScheduleUpdates } from '../../server/resolveActionScheduleUpdates';
import { duration } from '../../utils/Utils';
import { DelayedAction } from '../ReplicantAPI';
import { ScheduledActionsAPI } from '../ScheduledActionsAPI';

export type CreateScheduledActionsAPIOpts<TScheduledActions extends ScheduledActions> = {
    errorReporter: ErrorReporter;
    userId: string;
    userData: { metainfoMVCC: MetainfoMVCC };
    db: DB<any>;
    scheduledActions: TScheduledActions;
    invokeTime: number;
    delayedActions: DelayedAction[];
    isSystemAction?: boolean;
};

export function createScheduledActionsAPI<TScheduledActions extends ScheduledActions>(
    opts: CreateScheduledActionsAPIOpts<TScheduledActions>,
): ScheduledActionsAPI<TScheduledActions> {
    const { errorReporter, userId, userData, db, scheduledActions, invokeTime, delayedActions } = opts;

    const scheduleActionsMap: ScheduledActionsAPI<TScheduledActions>['schedule'] = {} as any;

    for (const actionName in scheduledActions) {
        // eslint-disable-next-line no-prototype-builtins
        if (scheduledActions.hasOwnProperty(actionName)) {
            scheduleActionsMap[actionName] = ({ args, notificationId, delayInMS, targetUserId }): void => {
                opts.scheduledActions[actionName]!.schema.tryValidate(args);

                const isValidDelay =
                    typeof delayInMS === 'number' &&
                    !Number.isNaN(delayInMS) &&
                    delayInMS >= 0 &&
                    delayInMS < Number.MAX_SAFE_INTEGER;

                if (!isValidDelay) {
                    throw Error(
                        `Invalid delayInMS in api.scheduledActions.schedule.${actionName}: ${delayInMS}. delayInMS must be a safe positive integer.`,
                    );
                }

                const maxDelayMs = duration({ days: 365 });
                if (delayInMS > maxDelayMs) {
                    throw Error(
                        `Invalid delayInMS in api.scheduledActions.schedule.${actionName}: ${delayInMS}. delayInMS must be less than 365 days in milliseconds.`,
                    );
                }

                delayedActions.push({
                    type: 'scheduleAction',
                    actionArgs: args,
                    actionName,
                    metainfoMVCC: targetUserId ? undefined : userData.metainfoMVCC,
                    notificationId,
                    userId: targetUserId || userId,
                    whenToExecute: invokeTime + delayInMS,
                    isSystemAction: opts.isSystemAction,
                });
            };
        }
    }

    function unschedule(unscheduleOpts: string | { notificationId: string; targetUserId?: string }): void {
        const unscheduleOptsObj =
            typeof unscheduleOpts === 'object' ? unscheduleOpts : { notificationId: unscheduleOpts };

        delayedActions.push({
            type: 'unscheduleAction',
            metainfoMVCC: unscheduleOptsObj.targetUserId ? undefined : userData.metainfoMVCC,
            notificationId: unscheduleOptsObj.notificationId,
            userId: unscheduleOptsObj.targetUserId || userId,
            isSystemAction: opts.isSystemAction,
        });
    }

    /**
     * Reschedule all actions for a user by some amount of milliseconds. Part of 'time machine', or setClockOffset API.
     * Should not be called in prod.
     */
    function rescheduleAllBy(offsetInMillis: number): void {
        const action = async () => {
            const notifications = opts?.isSystemAction
                ? userData.metainfoMVCC.systemScheduledActionNotifications
                : userData.metainfoMVCC.scheduledActionNotifications;

            const scheduledActions = await Promise.all(
                Object.values(notifications ?? {}).map(async (scheduledActionKey) =>
                    db.scheduledActions.fetch(scheduledActionKey),
                ),
            );

            const scheduleUpdates: (DelayedAction & { type: 'scheduleAction' })[] = scheduledActions
                .filter((action): action is ScheduledAction => !!action)
                .map((action) => {
                    const whenToExecute =
                        action.whenToExecute <= invokeTime
                            ? action.whenToExecute // it's already in the past, so don't update it
                            : Math.max(action.whenToExecute + offsetInMillis, invokeTime); // it's in the future so update it, but the earliest it can be is now

                    return {
                        actionArgs: action.actionArgs,
                        actionName: action.actionName,
                        metainfoMVCC: userData.metainfoMVCC,
                        notificationId: action.notificationId,
                        type: 'scheduleAction',
                        userId,
                        whenToExecute,
                        isSystemAction: action.isSystemAction,
                    };
                });

            await resolveActionScheduleUpdates({ db, errorReporter, now: invokeTime, scheduleUpdates });
        };

        delayedActions.push({ type: 'action', action, apiMethodName: 'api.scheduledActions.rescheduleAllBy' });
    }

    return {
        schedule: scheduleActionsMap,
        unschedule,
        rescheduleAllBy,
    };
}
